import { createContext, useContext, useState, type ReactNode } from "react";

export enum LoanType {
  PERSONAL = "personal",
  BUSINESS = "business",
  HOME = "home",
  STUDENT = "student",
  BALANCE_TRANSFER = "balance_transfer",
}

type FormData = {
  form: {
    type: LoanType;
  };
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    dateOfBirth: string;
    gender: string;
    panCard: string;
    residenceType: string;
    pincode: string;
    city: string;
    state: string;
  };
  personalLoan: {
    desiredLoanAmount: string;
    employmentType: string;
    monthlyIncome: string;
    yearsEmployed: string;
    primaryBank: string;
    totalEmi: string;
  };
  balanceTransfer: {
    balanceTransferType: string;
    desiredLoanAmount: string;
    profession: string;
    totalCTC: string;
    totalYears: string;
    outstandingPrincipal: string;
    presentInterestRate: string;
    remainingTenure: string;
    bankAccount: string;
  };
  homeLoan: {
    desiredLoanAmount: string;
    employmentType: string;
    monthlyIncome: string;
    yearsEmployed: string;
    primaryBank: string;
    totalEmi: string;
  };
  businessLoan: {
    desiredLoanAmount: string;
    annualGrossTurnover: string;
    businessVintage: string;
    companyType: string;
    industryType: string;
    natureOfBusiness: string;
    profitAfterTax: string;
    primaryBank: string;
    totalEmi: string;
  };
  studentLoan: {
    desiredLoanAmount: string;
    fatherEmploymentType: string;
    fatherMonthlyIncome: string;
    motherEmploymentType: string;
    motherMonthlyIncome: string;
    primaryBank: string;
    totalEmi: string;
    studentFullName: string;
    countryOfStudy: string;
    courseStartDate: string;
    courseDuration: string;
    courseName: string;
  };
};

type FormContextType = {
  currentStep: number;
  formData: FormData;
  updateFormData: (key: keyof FormData, value: any) => void;
  nextStep: () => void;
  prevStep: () => void;
  totalSteps: number;
};

const FormContext = createContext<FormContextType | undefined>(undefined);

const initialFormData: FormData = {
  form: {
    type: LoanType.PERSONAL,
  },
  personalDetails: {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    dateOfBirth: "",
    gender: "Male",
    panCard: "",
    residenceType: "",
    pincode: "",
    city: "",
    state: "",
  },
  personalLoan: {
    desiredLoanAmount: "",
    employmentType: "",
    monthlyIncome: "",
    yearsEmployed: "",
    primaryBank: "",
    totalEmi: "",
  },
  balanceTransfer: {
    balanceTransferType: "",
    desiredLoanAmount: "",
    profession: "",
    totalCTC: "",
    totalYears: "",
    outstandingPrincipal: "",
    presentInterestRate: "",
    remainingTenure: "",
    bankAccount: "",
  },
  homeLoan: {
    desiredLoanAmount: "",
    employmentType: "",
    monthlyIncome: "",
    yearsEmployed: "",
    primaryBank: "",
    totalEmi: "",
  },
  businessLoan: {
    desiredLoanAmount: "",
    annualGrossTurnover: "",
    businessVintage: "",
    companyType: "",
    industryType: "",
    natureOfBusiness: "",
    profitAfterTax: "",
    primaryBank: "",
    totalEmi: "",
  },
  studentLoan: {
    desiredLoanAmount: "",
    fatherEmploymentType: "",
    fatherMonthlyIncome: "",
    motherEmploymentType: "",
    motherMonthlyIncome: "",
    primaryBank: "",
    totalEmi: "",
    studentFullName: "",
    countryOfStudy: "",
    courseStartDate: "",
    courseDuration: "",
    courseName: "",
  },
};

export function FormProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState(0); // Start with welcome screen
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const totalSteps = 4; // Including welcome, phone, otp screens

  const updateFormData = (key: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // useEffect(() => {
  //   if(formData?.user?.memberships?.is_opc){
  //     setCurrentStep(5);
  //   }
  // },[formData])

  return (
    <FormContext.Provider
      value={{
        currentStep,
        formData,
        updateFormData,
        nextStep,
        prevStep,
        totalSteps,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useForm must be used within a FormProvider");
  }
  return context;
};
